// src/components/ui/EnviarBoletoModal.jsx
import React, { useState, useEffect } from "react";
import api from "../../services/api";

const fmtM = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtD = d => { try{ const s=String(d||"").split("T")[0]; return s?new Date(s+"T12:00:00").toLocaleDateString("pt-BR"):"—"; }catch{ return d||"—"; } };

// Formata número WhatsApp para exibição: 11999999999 → (11) 99999-9999
const fmtWpp = nr => {
  if (!nr) return "";
  const n = nr.replace(/\D/g,"");
  if (n.length === 11) return `(${n.slice(0,2)}) ${n.slice(2,7)}-${n.slice(7)}`;
  if (n.length === 10) return `(${n.slice(0,2)}) ${n.slice(2,6)}-${n.slice(6)}`;
  return nr;
};

// Limpa número para envio: (11) 99999-9999 → 5511999999999
const limparNr = nr => {
  const n = (nr||"").replace(/\D/g,"");
  if (n.startsWith("55")) return n;
  return "55" + n;
};

export default function EnviarBoletoModal({ ids=[], onClose, onSent }) {
  // ids pode ser um único id ou array de ids
  const idsList = Array.isArray(ids) ? ids : [ids];
  const isLote  = idsList.length > 1;

  const [dados,    setDados]    = useState(null);   // dados do primeiro boleto (preview)
  const [loading,  setLoading]  = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Form: canal + contatos editáveis
  const [canal,    setCanal]    = useState("ambos");  // 'email' | 'whatsapp' | 'ambos'
  const [email,    setEmail]    = useState("");
  const [wpp,      setWpp]      = useState("");
  const [editEmail, setEditEmail] = useState(false);
  const [editWpp,   setEditWpp]   = useState(false);

  useEffect(() => {
    if (!idsList[0]) return;
    setLoading(true);
    api("GET", `/boletos/${idsList[0]}/dados-envio`)
      .then(r => {
        setDados(r.data);
        setEmail(r.data.emailCondomino || "");
        setWpp(r.data.nrWhatsApp || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [idsList[0]]);

  // Determina quais canais estão disponíveis
  const temEmail = email.trim() !== "";
  const temWpp   = wpp.trim() !== "";
  const canalOk  = (canal === "email" && temEmail) ||
                   (canal === "whatsapp" && temWpp) ||
                   (canal === "ambos" && (temEmail || temWpp));

  const handleEnviar = async () => {
    setEnviando(true);
    setResultado(null);
    try {
      let r;
      if (isLote) {
        r = await api("POST", "/boletos/enviar-lote", {
          ids:             idsList,
          canal,
          emailOverride:   editEmail ? email : undefined,
          nrWhatsAppOverride: editWpp ? limparNr(wpp) : undefined,
        });
      } else {
        r = await api("POST", `/boletos/${idsList[0]}/enviar`, {
          canal,
          emailDestino:  email || undefined,
          nrWhatsApp:    limparNr(wpp) || undefined,
          nomeDestino:   dados?.nmCondomino,
        });
      }
      setResultado(r);
      if (r.success) onSent?.();
    } catch(e) {
      setResultado({ success:false, message:e.message });
    }
    setEnviando(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <h5 className="modal-title">
            <i className="bi bi-send-fill me-2" style={{color:"#25D366"}}/>
            {isLote ? `Enviar ${idsList.length} Boleto(s)` : "Enviar Boleto"}
          </h5>
          <button className="btn-close" onClick={onClose}/>
        </div>

        <div className="modal-body">
          {loading ? (
            <div style={{textAlign:"center",padding:"32px 0"}}>
              <span className="spinner-border text-primary"/>
            </div>
          ) : resultado ? (
            /* ── Resultado do envio ── */
            <div style={{textAlign:"center",padding:"16px 0"}}>
              <div style={{
                width:64,height:64,borderRadius:"50%",margin:"0 auto 12px",
                background:resultado.success?"#DCFCE7":"#FEE2E2",
                display:"flex",alignItems:"center",justifyContent:"center",
              }}>
                <i className={`bi ${resultado.success?"bi-check-circle-fill":"bi-x-circle-fill"}`}
                  style={{fontSize:32,color:resultado.success?"#166534":"#991B1B"}}/>
              </div>
              <div style={{fontWeight:700,fontSize:16,
                color:resultado.success?"#166534":"#991B1B",marginBottom:8}}>
                {resultado.message}
              </div>

              {/* Detalhes por canal */}
              {resultado.resultados && (
                <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:12}}>
                  {resultado.resultados.email && (
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",
                      background:resultado.resultados.email.sucesso?"#F0FDF4":"#FEF2F2",
                      borderRadius:8,fontSize:13}}>
                      <i className={`bi ${resultado.resultados.email.sucesso?"bi-check-circle-fill text-success":"bi-x-circle-fill text-danger"}`}/>
                      <span>E-mail: {resultado.resultados.email.sucesso?"Enviado":`Erro — ${resultado.resultados.email.erro}`}</span>
                    </div>
                  )}
                  {resultado.resultados.whatsapp && (
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",
                      background:resultado.resultados.whatsapp.sucesso?"#F0FDF4":"#FEF2F2",
                      borderRadius:8,fontSize:13}}>
                      <i className={`bi ${resultado.resultados.whatsapp.sucesso?"bi-check-circle-fill text-success":"bi-x-circle-fill text-danger"}`}/>
                      <span>WhatsApp: {resultado.resultados.whatsapp.sucesso?"Enviado":`Erro — ${resultado.resultados.whatsapp.erro}`}</span>
                    </div>
                  )}
                  {/* Detalhes lote */}
                  {resultado.enviados !== undefined && (
                    <div style={{marginTop:8,fontSize:13,color:"var(--gray-600)"}}>
                      ✅ {resultado.enviados} enviado(s) &nbsp;
                      {resultado.erros > 0 && <span style={{color:"#991B1B"}}>❌ {resultado.erros} erro(s)</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ── Preview do boleto ── */}
              {dados && !isLote && (
                <div style={{background:"var(--bg-surface-alt)",borderRadius:10,padding:"12px 14px",
                  marginBottom:16,border:"1px solid var(--gray-200)"}}>
                  <div style={{fontWeight:700,fontSize:13,color:"var(--gray-800)",marginBottom:8}}>
                    <i className="bi bi-receipt me-2" style={{color:"var(--primary)"}}/>
                    Boleto
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,fontSize:12.5}}>
                    <div style={{color:"var(--gray-500)"}}>Condômino</div>
                    <div style={{fontWeight:600}}>{dados.nmCondomino||"—"}</div>
                    <div style={{color:"var(--gray-500)"}}>Unidade</div>
                    <div style={{fontWeight:600}}>{dados.unidade}{dados.bloco?`/Bl.${dados.bloco}`:""}</div>
                    <div style={{color:"var(--gray-500)"}}>Competência</div>
                    <div style={{fontWeight:600}}>{dados.competencia||"—"}</div>
                    <div style={{color:"var(--gray-500)"}}>Valor</div>
                    <div style={{fontWeight:700,color:"var(--primary)"}}>{fmtM(dados.vlrBoleto)}</div>
                    <div style={{color:"var(--gray-500)"}}>Vencimento</div>
                    <div style={{fontWeight:600,color:dados.dtVencimento<new Date().toISOString().split("T")[0]?"#991B1B":"var(--gray-800)"}}>
                      {fmtD(dados.dtVencimento)}
                    </div>
                  </div>
                  {/* Linha digitável */}
                  {dados.linhaDigitavel && (
                    <div style={{marginTop:10,padding:"8px 10px",background:"var(--bg-surface)",
                      borderRadius:6,border:"1px solid var(--gray-200)"}}>
                      <div style={{fontSize:10,color:"var(--gray-400)",marginBottom:3}}>Linha Digitável</div>
                      <div style={{fontSize:11,fontFamily:"monospace",color:"var(--gray-700)",
                        wordBreak:"break-all"}}>{dados.linhaDigitavel}</div>
                    </div>
                  )}
                  {/* PIX */}
                  {dados.chavePix && (
                    <div style={{marginTop:6,padding:"6px 10px",background:"var(--bg-success-soft)",
                      borderRadius:6,border:"1px solid #BBF7D0",
                      display:"flex",alignItems:"center",gap:6}}>
                      <i className="bi bi-qr-code" style={{color:"#166534",fontSize:14}}/>
                      <div>
                        <div style={{fontSize:10,color:"#166534",fontWeight:600}}>PIX disponível</div>
                        <div style={{fontSize:11,color:"#166534",wordBreak:"break-all"}}>{dados.chavePix}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isLote && (
                <div style={{background:"var(--bg-highlight)",borderRadius:10,padding:"12px 14px",
                  marginBottom:16,border:"1px solid #BFDBFE",fontSize:13}}>
                  <i className="bi bi-info-circle me-2" style={{color:"#1D4ED8"}}/>
                  <strong style={{color:"#1D4ED8"}}>{idsList.length} boletos</strong>
                  <span style={{color:"#1D4ED8"}}> serão enviados para os contatos cadastrados de cada unidade.</span>
                </div>
              )}

              {/* ── Seleção de canal ── */}
              <div style={{marginBottom:16}}>
                <label className="form-label" style={{fontWeight:600}}>Enviar via</label>
                <div style={{display:"flex",gap:8}}>
                  {[
                    {v:"email",    icon:"bi-envelope-fill",   label:"E-mail",    color:"#1D4ED8", bg:"#EFF6FF"},
                    {v:"whatsapp", icon:"bi-whatsapp",        label:"WhatsApp",  color:"#166534", bg:"#DCFCE7"},
                    {v:"ambos",    icon:"bi-send-fill",       label:"Ambos",     color:"#7C3AED", bg:"#F5F3FF"},
                  ].map(op=>(
                    <button key={op.v} onClick={()=>setCanal(op.v)}
                      style={{flex:1,padding:"10px 8px",borderRadius:8,cursor:"pointer",
                        border:`2px solid ${canal===op.v?op.color:"var(--gray-200)"}`,
                        background:canal===op.v?op.bg:"#fff",
                        color:canal===op.v?op.color:"var(--gray-500)",
                        fontWeight:canal===op.v?700:400,fontSize:13,
                        display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                      <i className={`bi ${op.icon}`} style={{fontSize:18}}/>
                      {op.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Contatos ── */}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>

                {/* E-mail */}
                {(canal==="email"||canal==="ambos") && (
                  <div style={{padding:"10px 14px",borderRadius:8,
                    background:temEmail?"#F8FAFC":"#FEF2F2",
                    border:`1px solid ${temEmail?"var(--gray-200)":"#FECACA"}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",marginBottom:editEmail?8:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <i className="bi bi-envelope-fill" style={{color:"#1D4ED8",fontSize:14}}/>
                        <div>
                          <div style={{fontSize:11,color:"var(--gray-400)",fontWeight:600}}>E-MAIL</div>
                          {!editEmail && (
                            <div style={{fontSize:13,color:temEmail?"var(--gray-800)":"#991B1B",fontWeight:500}}>
                              {temEmail ? email : "Não cadastrado"}
                            </div>
                          )}
                        </div>
                      </div>
                      <button onClick={()=>setEditEmail(p=>!p)}
                        style={{background:"none",border:"none",cursor:"pointer",
                          color:"var(--gray-400)",fontSize:12,padding:"2px 6px"}}>
                        <i className={`bi bi-${editEmail?"check-lg":"pencil"}`}/>
                        {editEmail?" Confirmar":" Editar"}
                      </button>
                    </div>
                    {editEmail && (
                      <input className="form-control form-control-sm" type="email"
                        value={email} onChange={e=>setEmail(e.target.value)}
                        placeholder="email@exemplo.com"/>
                    )}
                    {!temEmail && !editEmail && (
                      <div style={{fontSize:11,color:"#991B1B",marginTop:4}}>
                        <i className="bi bi-exclamation-circle me-1"/>
                        Clique em editar para informar o e-mail
                      </div>
                    )}
                  </div>
                )}

                {/* WhatsApp */}
                {(canal==="whatsapp"||canal==="ambos") && (
                  <div style={{padding:"10px 14px",borderRadius:8,
                    background:temWpp?"#F8FAFC":"#FEF2F2",
                    border:`1px solid ${temWpp?"var(--gray-200)":"#FECACA"}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",marginBottom:editWpp?8:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <i className="bi bi-whatsapp" style={{color:"#25D366",fontSize:14}}/>
                        <div>
                          <div style={{fontSize:11,color:"var(--gray-400)",fontWeight:600}}>WHATSAPP</div>
                          {!editWpp && (
                            <div style={{fontSize:13,color:temWpp?"var(--gray-800)":"#991B1B",fontWeight:500}}>
                              {temWpp ? fmtWpp(wpp) : "Não cadastrado"}
                            </div>
                          )}
                        </div>
                      </div>
                      <button onClick={()=>setEditWpp(p=>!p)}
                        style={{background:"none",border:"none",cursor:"pointer",
                          color:"var(--gray-400)",fontSize:12,padding:"2px 6px"}}>
                        <i className={`bi bi-${editWpp?"check-lg":"pencil"}`}/>
                        {editWpp?" Confirmar":" Editar"}
                      </button>
                    </div>
                    {editWpp && (
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">+55</span>
                        <input className="form-control" type="tel"
                          value={wpp.replace(/\D/g,"").replace(/^55/,"")}
                          onChange={e=>setWpp(e.target.value.replace(/\D/g,""))}
                          placeholder="11999999999"/>
                      </div>
                    )}
                    {!temWpp && !editWpp && (
                      <div style={{fontSize:11,color:"#991B1B",marginTop:4}}>
                        <i className="bi bi-exclamation-circle me-1"/>
                        Clique em editar para informar o número
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!canalOk && (
                <div style={{marginTop:10,padding:"8px 12px",background:"var(--bg-warning-soft)",
                  borderRadius:6,fontSize:12.5,color:"#854D0E"}}>
                  <i className="bi bi-exclamation-triangle me-1"/>
                  Informe ao menos um contato válido para o canal selecionado.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {resultado ? (
            <button className="btn btn-outline-secondary" onClick={onClose}>Fechar</button>
          ) : (
            <>
              <button className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button className="btn btn-success" onClick={handleEnviar}
                disabled={enviando||!canalOk||loading}>
                {enviando
                  ? <><span className="spinner-border spinner-border-sm me-2"/>Enviando...</>
                  : <>
                      <i className="bi bi-send-fill me-2"/>
                      {canal==="email"?"Enviar E-mail":canal==="whatsapp"?"Enviar WhatsApp":"Enviar E-mail + WhatsApp"}
                    </>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
